import React from "react";
import {
  VariantControl,
  VariantDot,
  type VariantType,
} from "../../components/variant/VariantControl";
import type { NewPropertyState } from "../../../usePropertyStateV2";
import { type ComboBoxOptionType, DiscreteInput } from "../../components/input";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { TEXT_DECORATION_LINE_OPTIONS } from "../../components/variant/variantOptions";
import { PropertyType } from "@/tailwind/tailwindUtils";

// TODO: Split all fields out from the TailwindStylingPanelV2.tsx file like this

interface TextDecorationProps {
  textDecorationLineState: NewPropertyState;
  pickTailwindClassForProperty: (props: {
    option: ComboBoxOptionType;
    propertyType: PropertyType;
    setTemporaryDraggingValue: (value: string | null) => void;
    variant?: string | null;
  }) => void;
  clearTailwindClassForProperty: (props: {
    propertyType: PropertyType;
    setTemporaryDraggingValue: (value: string | null) => void;
    variant?: string | null;
  }) => void;
}

const TextDecorationField: React.FC<TextDecorationProps> = React.memo(
  ({
    textDecorationLineState,
    pickTailwindClassForProperty,
    clearTailwindClassForProperty,
  }) => {
    return (
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div className="flex flex-1 w-full items-center gap-2 justify-between relative">
            <VariantDot
              valueByVariant={textDecorationLineState.valueByVariant}
            />
            <DiscreteInput
              selected={textDecorationLineState.propertyValue}
              set={textDecorationLineState.isSet}
              onOptionSelected={(discreteOption) => {
                pickTailwindClassForProperty({
                  option: discreteOption.option,
                  propertyType: PropertyType.TEXT_DECORATION_LINE,
                  setTemporaryDraggingValue:
                    textDecorationLineState.setTemporaryDraggingValue,
                });
              }}
              onDeselect={() => {
                clearTailwindClassForProperty({
                  propertyType: PropertyType.TEXT_DECORATION_LINE,
                  setTemporaryDraggingValue:
                    textDecorationLineState.setTemporaryDraggingValue,
                });
              }}
              className="w-full"
              options={TEXT_DECORATION_LINE_OPTIONS}
            />
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent className="p-0 border-none">
          <VariantControl
            valueByVariant={textDecorationLineState.valueByVariant}
            onVariantChange={(variant: VariantType, value) => {
              const option = TEXT_DECORATION_LINE_OPTIONS.find(
                (opt) => opt.key === value,
              );
              if (option) {
                pickTailwindClassForProperty({
                  option: option.option,
                  propertyType: PropertyType.TEXT_DECORATION_LINE,
                  setTemporaryDraggingValue:
                    textDecorationLineState.setTemporaryDraggingValue,
                  variant,
                });
              }
            }}
            onClearVariant={(variant: VariantType) => {
              clearTailwindClassForProperty({
                propertyType: PropertyType.TEXT_DECORATION_LINE,
                setTemporaryDraggingValue:
                  textDecorationLineState.setTemporaryDraggingValue,
                variant,
              });
            }}
            currentFlexDirection={""}
            currentJustifyContent={""}
            currentAlignItems={""}
            controlType="text-decoration-line"
          >
            {(variant, expanded, setExpanded) => {
              if (!expanded || !variant) return null;
              const variantValue =
                textDecorationLineState.valueByVariant[variant]?.value || "";
              return (
                <DiscreteInput
                  options={TEXT_DECORATION_LINE_OPTIONS}
                  onOptionSelected={(discreteOption) => {
                    pickTailwindClassForProperty({
                      option: discreteOption.option,
                      propertyType: PropertyType.TEXT_DECORATION_LINE,
                      setTemporaryDraggingValue:
                        textDecorationLineState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  selected={variantValue}
                  set={!!variantValue}
                  onDeselect={() => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.TEXT_DECORATION_LINE,
                      setTemporaryDraggingValue:
                        textDecorationLineState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                />
              );
            }}
          </VariantControl>
        </ContextMenuContent>
      </ContextMenu>
    );
  },
);

export default TextDecorationField;
